import { isEqual } from "es-toolkit";
import { first, isEmpty } from "es-toolkit/compat";
import { atom } from "jotai";
import { atomWithCache } from "jotai-cache";
import { atomEffect } from "jotai-effect";
import { atomFamily, atomWithRefresh, loadable } from "jotai/utils";

import { Logger } from "@moment/logging";

import { getCache } from "../_interop/cache";
import { $accessTokenFunc, $isAuthenticated, $loginContextGuard } from "../authenication";
import { LOCAL_WORKSPACE_NAME } from "../constants";
import { disk } from "../disk";
import { $docsApi } from "../docsapi";
import { $workspaceCollabSessions } from "../document-collab-registry";
import { metadataKeyForDocsRoute } from "../document-metadata/documentMetadataKey";
import { $documentMetadata } from "../document-metadata/metadata-state.atom";
import { $remoteWorkspace } from "../document/sharing/permissions.atom";
import { $metadataCollabConnection } from "../reboot/metadata-collab-connection.atom";
import { type DocumentRefProps } from "../reboot/refs";
import { getLatestMetadata } from "../reboot/utils/metadata-utils";
import { $internet } from "../utils";
import {
	type DocsListItem,
	type DocsListRemoteEntry,
	type DocumentMetadata,
	docsListItemLocalEntry,
	docsListItemType,
} from "./document-list.types";
import { $templateDocuments } from "./template-documents.atom";
import { toDocumentListItem } from "./toDocumentListItem";

// Optimistic documents atom for immediate updates before cache persistence.
// Draft documents represent documents created this session that disk/remote don't know about yet.
const $draftDocuments = atom({} as Record<string, DocsListItem>);
$draftDocuments.debugLabel = "draftDocuments";

/**
 * Writable atom to add a document to the draft list.
 * Use this when creating a new document to make it appear immediately in the documents list.
 */
export const $addDraftDocument = atom(null, (get, set, document: DocsListItem) => {
	const current = get($draftDocuments);
	set($draftDocuments, { ...current, [document.id]: document });
});
$addDraftDocument.debugLabel = "addDraftDocument";

// Optimistic deletion tracking - documents marked as deleted will be filtered out of the UI
// before the underlying sources (disk, remote, cache) are refreshed.
const $deletedDocumentIds = atom(new Set<string>());
$deletedDocumentIds.debugLabel = "deletedDocumentIds";

/**
 * Writable atom to mark a document as deleted for optimistic UI updates.
 * Use this when deleting a document to remove it immediately from the documents list.
 */
export const $removeDocumentFromDocsList = atom(null, (get, set, documentId: string) => {
	const current = get($deletedDocumentIds);
	set($deletedDocumentIds, new Set([...current, documentId]));
});
$removeDocumentFromDocsList.debugLabel = "removeDocumentFromDocsList";

/**
 * Writable atom to restore a document after optimistic deletion fails.
 */
export const $restoreDocumentInDocsList = atom(null, (get, set, documentId: string) => {
	const current = get($deletedDocumentIds);
	if (!current.has(documentId)) {
		return;
	}

	const next = new Set(current);
	next.delete(documentId);
	set($deletedDocumentIds, next);
});
$restoreDocumentInDocsList.debugLabel = "restoreDocumentInDocsList";

const logger = new Logger("documents-atom");

export interface CachedDocsListItem extends DocsListItem {
	error?: string;
}

export interface CachedDocumentsList {
	status: "initializing" | "idle" | "refreshing";
	documents: Record<string, CachedDocsListItem>;
}

const $documentsListValue = atom({ status: "initializing", documents: {} } as CachedDocumentsList);
const $documentIds = atom((get) => Object.keys(get($documentsList).documents));

export const $documentsList = atom((get) => get($documentsListValue));

/**
 * The core reconciliation loop for the documents list: this effect subscribes to all the sources of
 * data for a document, and and merges them into a single docs list that is cached in SQLite.
 */
export const $documentsListEffect = atomEffect((get, set) => {
	// !!! IMPORTANT: THE EFFECT'S SUBSCRIPTIONS !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
	// !!! This effect will run any time any of these things change. Put all subscriptions here to
	// !!! help keep track.
	const cacheLoadable = get(loadable($cacheDocuments));
	const diskLoadable = get(loadable($diskDocuments));
	const remoteLoadable = get(loadable($remoteDocuments));
	const currentStatus = get.peek($documentsListValue).status;
	const collabSessions = get($workspaceCollabSessions);
	const draftDocuments = get($draftDocuments);
	const templateDocuments = get($templateDocuments);
	const deletedDocumentIds = get($deletedDocumentIds);
	const documentCollabMetadata = get($documentCollabMetadata);
	// !!! NOTE: Additional dynamic subscriptions are created below (lines ~290-311) where
	// !!! get($documentMetadata(metadataKey)) is called for each document in the list.
	// !!! These cause the effect to re-run when any loaded document's metadata changes.
	// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!

	// Do nothing if the cache has not loaded.
	if (cacheLoadable.state !== "hasData") {
		logger.debug("Waiting for cache to load");
		return;
	}

	// Fast path: ONLY cache loaded, since disk and remote still loading. Nothing to merge with the
	// cached docs list state.
	if (diskLoadable.state === "loading" && remoteLoadable.state === "loading") {
		logger.debug("Fast path: populating from cache only");
		// Preserve "initializing" status until first successful load
		const fastPathStatus = currentStatus === "initializing" ? "initializing" : "refreshing";
		set($documentsListValue, { status: fastPathStatus, documents: cacheLoadable.data });
		return;
	}

	// If we've already completed a full merge (status is "idle") and remote is just refreshing,
	// keep the current documents list stable. The previous fully-enriched state is better than
	// a degraded intermediate state built from the initial cache without remote data.
	if (currentStatus === "idle" && remoteLoadable.state === "loading") {
		return;
	}

	const diskSettled = diskLoadable.state === "hasData" || diskLoadable.state === "hasError";
	const remoteSettled = remoteLoadable.state === "hasData" || remoteLoadable.state === "hasError";

	const status: "initializing" | "idle" | "refreshing" =
		diskSettled && remoteSettled ? "idle" : "refreshing";
	// Begin merging the documents together.
	const documents: Record<string, CachedDocsListItem> = {};
	Object.assign(documents, cacheLoadable.data);

	// Merge disk documents into the documents list.
	if (diskLoadable.state === "hasData") {
		Object.entries(diskLoadable.data).forEach(([id, diskDocument]) => {
			const cachedDoc = documents[id];
			documents[id] = {
				...documents[id],
				id,
				local: { ...cachedDoc?.local, [diskDocument.path]: diskDocument },
				remote: { ...cachedDoc?.remote },
			};
		});
	}

	// Merge remote documents into the documents list.
	if (remoteLoadable.state === "hasData") {
		Object.entries(remoteLoadable.data).forEach(([id, remoteDocument]) => {
			const cachedDoc = documents[id];

			// Find collab sessions for this document from its workspace
			const workspaceSlug = remoteDocument.workspace.slug;
			const docCollabInfo = collabSessions[workspaceSlug]?.[id];

			// Determine collab data with proper fallback:
			// 1. Use live collab sessions from the Reboot stream if available
			// 2. Fall back to existing collab data from the cache (preserves collab info
			//    between app restarts while the collab registry stream is still connecting)
			// 3. Fall back to empty object only if neither source has collab data
			const cachedRemoteEntry = cachedDoc?.remote?.[remoteDocument.workspace.id];
			const existingCollab = cachedRemoteEntry?.collab ?? {};
			const collab = docCollabInfo?.sessions ?? existingCollab;

			// For collab documents, preserve cached title/updatedAt/type since the cache
			// stores enriched collab metadata, while DocsAPI returns stale git-level metadata.
			// Without this, on app reload the list briefly shows "Untitled" and wrong dates
			// until the collab metadata effect re-loads fresh data from Reboot.
			const hasCache = !isEmpty(existingCollab) && cachedRemoteEntry;

			documents[id] = {
				...documents[id],
				id,
				local: { ...cachedDoc?.local },
				remote: {
					...cachedDoc?.remote,
					[remoteDocument.workspace.id]: {
						...remoteDocument,
						...(hasCache
							? {
									title: cachedRemoteEntry.title || remoteDocument.title,
									updatedAt:
										cachedRemoteEntry.updatedAt > remoteDocument.updatedAt
											? cachedRemoteEntry.updatedAt
											: remoteDocument.updatedAt,
									type: cachedRemoteEntry.type || remoteDocument.type,
								}
							: {}),
						collab,
					},
				},
			};
		});
	}

	if (documentCollabMetadata) {
		Object.entries(documents).forEach(([documentId, document]) => {
			Object.values(document.remote).forEach((remote) => {
				Object.values(remote.collab).forEach((session) => {
					const key = `${documentId}.${remote.workspace.slug}.${session.branch}@${session.sha}`;
					const metadata = documentCollabMetadata[key];
					if (metadata) {
						documents[documentId] = {
							...documents[documentId],
							remote: {
								...documents[documentId].remote,
								[remote.workspace.id]: {
									...remote,
									title: metadata.title,
									updatedAt: new Date(metadata.updated),
									type: metadata.type,
								},
							},
						};
					}
				});
			});
		});
	}

	// Prune stale cache entries when both sources are fully loaded.
	// Transient failures (offline, auth errors, API errors) are already handled by
	// $remoteDocuments throwing, which puts the loadable in "hasError" state and
	// prevents reaching this branch. A successful fetch returning {} is legitimate
	// (user deleted all shared documents) and should prune stale remote entries.
	if (diskLoadable.state === "hasData" && remoteLoadable.state === "hasData") {
		Object.keys(documents).forEach((id) => {
			if (!diskLoadable.data[id] && !remoteLoadable.data[id]) {
				delete documents[id];
			}
		});
	}

	// Merge draft documents AFTER pruning so newly created documents aren't deleted.
	// Drafts represent documents created this session that disk/remote don't know about yet.
	Object.entries(draftDocuments).forEach(([id, draft]) => {
		const existing = documents[id];
		documents[id] = {
			id,
			local: { ...existing?.local, ...draft.local },
			remote: { ...existing?.remote, ...draft.remote },
		};
	});

	// Merge template documents AFTER pruning so template previews work.
	// Templates are remote documents from moment-templates workspace that should be
	// treated as read-only (no local, no collab). They are not persisted to cache.
	Object.entries(templateDocuments).forEach(([id, template]) => {
		const existing = documents[id];
		documents[id] = {
			id,
			local: { ...existing?.local, ...template.local },
			remote: { ...existing?.remote, ...template.remote },
		};
	});

	// Filter out deleted documents for optimistic UI updates.
	// Documents marked as deleted are removed before sources refresh.
	deletedDocumentIds.forEach((id) => {
		delete documents[id];
	});

	// Apply live titles from in-memory document metadata.
	// $documentMetadata is the single source of truth for loaded documents and always has the
	// most up-to-date title. Disk/remote/collab sources may lag behind (e.g. after a rename,
	// the disk write or collab roundtrip hasn't completed yet). Reading directly from
	// $documentMetadata ensures the docs list stays in sync without needing explicit
	// invalidation of upstream sources. When authoritative sources eventually catch up, they
	// naturally converge with the in-memory state.
	for (const docId of Object.keys(documents)) {
		const metadataKey = metadataKeyForDocsRoute(docId);
		const metadata = get($documentMetadata(metadataKey));
		// Intentional loose equality: skip only when metadata is null or undefined
		// (document not yet loaded). A loaded document with an empty-string title
		// should still patch the docs list to avoid retaining stale titles.
		if (metadata == null) {
			continue;
		}

		const doc = documents[docId];
		const patchedLocal = Object.fromEntries(
			Object.entries(doc.local).map(([path, entry]) => [
				path,
				{ ...entry, title: metadata.title, updatedAt: metadata.updated ?? entry.updatedAt },
			])
		) as typeof doc.local;
		const patchedRemote = Object.fromEntries(
			Object.entries(doc.remote).map(([wsId, entry]) => [
				wsId,
				{ ...entry, title: metadata.title, updatedAt: metadata.updated ?? entry.updatedAt },
			])
		) as typeof doc.remote;
		documents[docId] = { ...doc, local: patchedLocal, remote: patchedRemote };
	}

	// Update the documents list value
	set($documentsListValue, { status, documents });

	// Persist changes to SQLite cache only once the full view is materialized
	// (both disk and remote sources have resolved successfully). This avoids writing
	// partial/intermediate states to the cache on every effect re-run (collab updates,
	// metadata changes, etc.). When remote is in an error state (e.g. offline), we skip
	// persistence to avoid overwriting cached remote documents with an incomplete list.
	// Exclude template documents from persistence - they are temporary preview documents.
	if (diskSettled && remoteLoadable.state === "hasData") {
		const cache = getCache();
		const templateIds = new Set(Object.keys(templateDocuments));
		const documentsToUpsert = Object.values(documents).filter((document) => {
			if (templateIds.has(document.id)) {
				return false;
			}
			if (isEqual(document, cacheLoadable.data[document.id])) {
				return false;
			}
			// Don't persist collab documents until collab metadata has enriched them.
			// Without this guard, the first cache write after app startup would overwrite
			// previously-cached collab metadata (title, updatedAt) with stale DocsAPI
			// values, since $documentCollabMetadata hasn't been populated yet.
			const hasCollabSessions = Object.values(document.remote).some(
				(r) => !isEmpty(r.collab)
			);
			if (hasCollabSessions && isEmpty(documentCollabMetadata)) {
				return false;
			}
			return true;
		});
		if (documentsToUpsert.length > 0) {
			void cache.documents.upsertAll(documentsToUpsert);
		}
	}
});
$documentsListEffect.debugLabel = "documentsListEffect";

const $documentCollabMetadata = atom({} as Record<string, DocumentMetadata>);
$documentCollabMetadata.debugLabel = "documentCollabMetadata";

export const $documentCollabMetadataEffect = atomEffect((get, set) => {
	// Subscribe to documentIds so we re-run when documents are added/removed
	get($documentIds);
	// Subscribe to workspaceCollabSessions so we re-run when collab sessions arrive for
	// documents that are already in the list (e.g. a newly-shared document discovered via polling
	// whose collab session data arrives after the document was first added with collab: {}).
	get($workspaceCollabSessions);

	const documents = get.peek($documentsList);

	const documentCollabMetadata: Record<string, DocumentMetadata> = {};

	const loadables = Object.values(documents.documents).flatMap((document) => {
		if (isEmpty(document.remote)) {
			return;
		}

		return Object.values(document.remote).map((remote) => {
			// We only care about the first collab session right now because we only support one collab session per document anyway
			const session = Object.values(remote.collab)[0];
			if (!session) {
				return;
			}

			const metadataLoadable = get(
				loadable(
					$documentCollabMetadataSource({
						workspaceSlug: remote.workspace.slug,
						documentId: document.id,
						branch: session.branch,
						sha: session.sha,
					})
				)
			);

			if (metadataLoadable.state === "hasData" && metadataLoadable.data) {
				documentCollabMetadata[
					`${document.id}.${remote.workspace.slug}.${session.branch}@${session.sha}`
				] = metadataLoadable.data;
			}

			return metadataLoadable;
		});
	});

	// For right now, if we're still loading any of the metadata, don't set the documentCollabMetadata atom.
	// This is to keep Jotai from re-rendering the entire documents list every time a promise status changes.
	if (
		loadables
			.filter((loadable) => loadable !== undefined)
			.some((loadable) => loadable.state === "loading")
	) {
		return;
	}

	// If the state didn't actually change, don't set the atom
	if (isEqual(documentCollabMetadata, get.peek($documentCollabMetadata))) {
		return;
	}

	set($documentCollabMetadata, documentCollabMetadata);
});

/**
 * Fetches document metadata from a collab session connection.
 * Used to get metadata (title, updated) for documents with active collab sessions.
 */
const $documentCollabMetadataSource = atomFamily((props: DocumentRefProps) => {
	const metadataAtom = atomWithCache(async (get) => {
		const connection = await get($metadataCollabConnection(props));
		if (!connection) {
			return undefined;
		}
		return getLatestMetadata(connection.store);
	});
	metadataAtom.debugLabel = `documentCollabMetadata/${props.workspaceSlug}/${props.documentId}`;
	return metadataAtom;
}, isEqual);

export const $remoteRepoExists = atomFamily((documentId: string) => {
	const $remoteExists = atomWithCache(async (get) => {
		const document = get($document(documentId));
		if (!document) {
			return undefined;
		}

		const remoteWorkspaceFromPermissions = await get($remoteWorkspace(documentId));
		if (remoteWorkspaceFromPermissions) {
			return true;
		}

		if (document.remote && Object.keys(document.remote).length > 0) {
			return true;
		}

		return false;
	});
	$remoteExists.debugLabel = `remoteExists/${documentId}`;

	return $remoteExists;
});

export const $documentCollabEnabled = atomFamily((documentId: string) => {
	const $documentCollabEnabledAtom = atom((get) => {
		const document = get($document(documentId));
		if (!document) {
			return undefined;
		}

		if (isEmpty(document.remote)) {
			return false;
		}

		const remote = first(Object.values(document.remote));
		if (!remote) {
			return false;
		}

		if (!isEmpty(remote.collab)) {
			return true;
		}

		return false;
	});
	$documentCollabEnabledAtom.debugLabel = `documentCollabEnabled/${documentId}`;

	return $documentCollabEnabledAtom;
});

export const $document = atomFamily((documentId: string | undefined) => {
	const $documentAtom = atom((get) => {
		if (!documentId) {
			return undefined;
		}

		const documentsList = get($documentsList);
		const fromList = documentsList.documents[documentId];
		if (fromList) {
			return fromList;
		}

		// Fallback: check draft documents directly (not yet merged by effect)
		const draftDocuments = get($draftDocuments);
		if (draftDocuments[documentId]) {
			return draftDocuments[documentId];
		}

		// Fallback: check template documents directly (for minimal mode where
		// $documentsListEffect doesn't run to merge template docs into the main list)
		const templateDocs = get($templateDocuments);
		return templateDocs[documentId];
	});
	$documentAtom.debugLabel = `document/${documentId}`;

	return $documentAtom;
});

export const $documentLocalPath = atomFamily((documentId: string) => {
	const $documentLocalPathAtom = atomWithCache(async (get) => {
		const document = get($document(documentId));
		if (!document) {
			return undefined;
		}

		if (!document.local) {
			return;
		}

		return docsListItemLocalEntry(document)?.path;
	});
	$documentLocalPathAtom.debugLabel = `documentLocalPath/${documentId}`;

	return $documentLocalPathAtom;
});

/**
 * Returns whether a document exists on disk (has local files)
 */
export const $documentHasLocal = atomFamily((documentId: string) => {
	const $documentHasLocalAtom = atomWithCache(async (get) => {
		const document = get($document(documentId));
		if (!document) {
			return false;
		}

		return !!document.local && Object.keys(document.local).length > 0;
	});
	$documentHasLocalAtom.debugLabel = `documentHasLocal/${documentId}`;

	return $documentHasLocalAtom;
}, isEqual);

const $cacheDocuments = atom(async (get) => {
	await get($loginContextGuard);
	const cache = getCache();
	const docs = await cache.documents.getAll();
	return docs;
});
$cacheDocuments.debugLabel = "cacheDocuments";

const $diskDocuments = atom((get) => get(disk().documents.getAll));
$diskDocuments.debugLabel = "diskDocuments";

const $remoteDocuments = atomWithRefresh(
	async (get): Promise<Record<string, DocsListRemoteEntry>> => {
		const docsApi = get($docsApi);
		const { getAccessTokenSilently } = get($accessTokenFunc);

		if (!getAccessTokenSilently) {
			throw new Error("Auth token function unavailable");
		}

		// When offline, remote data is unavailable. Throw so loadable() captures
		// this as "hasError" — the documents list effect skips pruning and cache
		// persistence on error, preserving cached remote documents.
		const isOnline = get($internet);
		if (!isOnline) {
			throw new Error("Offline: remote documents unavailable");
		}

		// Errors propagate so loadable() captures them as "hasError".
		// The documents list effect already handles this correctly:
		//   - Pruning only runs when both disk AND remote are "hasData"
		//   - Cache persistence only runs when remote is "hasData"
		// Previously, catching errors and returning {} caused the effect to treat
		// a failed fetch as a successful empty result, pruning all remote-only
		// documents from the list and overwriting the cache.
		try {
			const token = await getAccessTokenSilently();
			if (!token) {
				throw new Error("Unauthenticated: remote documents unavailable");
			}
			const documents = await docsApi.queryDocuments(token);

			const data: Record<string, DocsListRemoteEntry> = Object.fromEntries(
				documents.map((doc) => [
					doc.id,
					{
						id: doc.id,
						title: doc.title,
						createdAt: new Date(doc.createdAt),
						updatedAt: new Date(doc.updatedAt),
						type: doc.type,
						workspace: doc.workspace,
						collab: {},
						permission: doc.permission,
					} as DocsListRemoteEntry,
				])
			);

			return data;
		} catch (error) {
			logger.error("Failed to fetch remote documents", { error });
			throw error;
		}
	}
);
$remoteDocuments.debugLabel = "remoteDocuments";

/**
 * Trigger an immediate refresh of the remote documents list.
 */
export const $refreshRemoteDocuments = atom(null, (_get, set) => {
	set($remoteDocuments);
});
$refreshRemoteDocuments.debugLabel = "refreshRemoteDocuments";

/**
 * Filtered and sorted documents based on facet (workspace filter).
 */
export const $filteredDocuments = atomFamily((facet: string | null) => {
	const $filteredDocumentsAtom = atom((get) => {
		const documentsState = get($documentsList);

		// when filtering documents, use a copy.
		const documents = structuredClone(documentsState.documents);

		let result: DocsListItem[];

		// Filter based on facet
		if (facet === null) {
			result = Object.values(documents);
		} else if (facet === LOCAL_WORKSPACE_NAME) {
			result = Object.values(documents).filter(
				(document) => !isEmpty(document.local) && isEmpty(document.remote)
			);
		} else {
			result = Object.values(documents).filter((document) => {
				return Object.values(document.remote).some(
					(remote) => remote.workspace.name === facet
				);
			});
		}

		// Filter out Notes documents - they are shown in a pinned section
		result = result.filter((document) => docsListItemType(document) !== "notes");

		// Filter out template preview documents - they are temporary and should not appear in the list
		const templateDocs = get($templateDocuments);
		result = result.filter((document) => !templateDocs[document.id]);

		const isAuthenticated = get($isAuthenticated);
		const isDisconnected = !get($internet);

		// Convert to DocumentListItem
		let docslist = Object.values(result).map(toDocumentListItem);

		// Sort by last modified date
		docslist = docslist.sort((a, b) => {
			const aDate = a.updatedAt;
			const bDate = b.updatedAt;
			return bDate.getTime() - aDate.getTime();
		});

		// If disconnected or not authenticated, sort so that local-only docs are at the top
		if (isDisconnected || !isAuthenticated) {
			docslist = docslist.sort((a, b) => {
				if (a.remote?.workspace === undefined && b.remote?.workspace !== undefined) {
					return -1;
				}
				if (a.remote?.workspace !== undefined && b.remote?.workspace === undefined) {
					return 1;
				}
				return 0;
			});
		}

		return docslist;
	});
	$filteredDocumentsAtom.debugLabel = `filteredDocuments/${facet}`;

	return $filteredDocumentsAtom;
});
